import { Col, message, Row } from "antd";
import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import Button from "@components/Button";
import Table from "@components/Table";
import {
  ErrorServices,
  useFetchLongTermIncentivesQuery,
  useDeleteLongTermIncentiveMutation,
  useUploadLongTermIncentivesMutation,
  longTermIPApi,
  useFetchLongTermIncentivesByPlanQuery,
} from "@services";
import { getColumns } from "./config";
import { ILongTermIncentive } from "@types";
import AddLongTermIncentive from "./AddIncentive";
import LongTermIncentiveVersions from "./Versions";
import Upload from "@components/Upload";
import { useTypedSelector } from "@hooks";
import { checkPermission } from "@/utils";
import { permissions } from "@/router";
import CustomButton from "@/components/customButton";
import { MdOutlineFileDownload, MdOutlineFileUpload } from "react-icons/md";
import SearchBox from "@/components/searchInput";
import customErrorMessage from "@/components/CustomErrorMessage";

const LongTermIncentives = () => {
  const { company, role } = useTypedSelector(state => state.auth?.user!);
  const { plan_id } = useParams<{ plan_id?: string }>();
  const { id: company_id } = company!;
  const dispatch = useDispatch();
  const longTermIncentive_id = useRef<any>(null);
  const [selectedLongTermIncentive, setSelectedLongTermIncentive] =
    useState<ILongTermIncentive | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isVersionsModal, setIsVersionsModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [isVisibleForEdit, setIsVisibleForEdit] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  const [page, setPage] = useState(1);
  const [deleteLongTermIncentive, { isLoading: isDeleting }] =
    useDeleteLongTermIncentiveMutation();
  const [isUploadModal, setIsUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState({
    file: null,
    active: false,
  });
  const [uploadLongTermIncentives, { isLoading: isUploading }] =
    useUploadLongTermIncentivesMutation();
  const {
    data: incentivesData,
    isLoading,
    refetch,
  } = useFetchLongTermIncentivesQuery(
    {
      company_id,
      page,
      page_size: pageSize,
    },
    { skip: !!plan_id === true }
  );
  const { data: incentives, pagination: incentivesPagination } =
    incentivesData || {};
  const { data: incentivesByPlanData, isLoading: isFetchingPlanIncentives } =
    useFetchLongTermIncentivesByPlanQuery(
      {
        company_id,
        plan_id,
        page,
      },
      { skip: !!plan_id === false }
    );
  const { data: incentivesByPlan, pagination: incentivesByPlanPagination } =
    incentivesByPlanData || {};

  const data = plan_id ? incentivesByPlan : incentives;
  const pagination = plan_id
    ? incentivesByPlanPagination
    : incentivesPagination;

  const editLongTermIncentive = (incentive: ILongTermIncentive) => {
    setSelectedLongTermIncentive(incentive);
    setIsVisibleForEdit(true);
  };

  const removeLongTermIncentive = async (id: number) => {
    try {
      longTermIncentive_id.current = id;
      await deleteLongTermIncentive({ company_id, id });
      message.success("Long term incentive deleted successfully!");
    } catch (error) {
      ErrorServices(error);
    }
  };

  const handleUpload = async () => {
    const { file, active }: { file: any; active: boolean } = uploadFile;
    setIsUploadModal(false);
    try {
      const formData = new FormData();
      formData.append("attachment", file!, file?.name!);
      const res = await uploadLongTermIncentives({
        company_id,
        active,
        body: formData,
      }).unwrap();
      message.success(res?.message);
    } catch (error) {
      customErrorMessage(error?.message);
    }
  };
  useEffect(() => {
    refetch();
  }, []);

  const download = (id?: number) => {
    dispatch(
      longTermIPApi.endpoints.downloadLongTermIncentives.initiate(
        {
          company_id,
          version_id: id || null,
        },
        {
          subscribe: false,
          forceRefetch: true,
        }
      )
    );
  };

  return (
    <>
      {isUploadModal && (
        <Upload
          title="Upload Long Term Incentives"
          onSubmit={handleUpload}
          isVisible={isUploadModal}
          setIsVisible={setIsUploadModal}
          file={uploadFile}
          setFile={setUploadFile}
        />
      )}
      {isVisible && (
        <AddLongTermIncentive
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          selectedLongTermIncentive={selectedLongTermIncentive}
          setSelectedLongTermIncentive={setSelectedLongTermIncentive}
        />
      )}
      {isVisibleForEdit && selectedLongTermIncentive?.id && (
        <AddLongTermIncentive
          isVisible={isVisibleForEdit}
          setIsVisible={() => {
            setIsVisibleForEdit(false);
            setSelectedLongTermIncentive(null);
          }}
          selectedLongTermIncentive={selectedLongTermIncentive}
          setSelectedLongTermIncentive={setSelectedLongTermIncentive}
          isEdit={true}
        />
      )}
      {isVersionsModal && (
        <LongTermIncentiveVersions
          isVisible={isVersionsModal}
          setIsVisible={setIsVersionsModal}
          download={download}
        />
      )}

      <div className="bg-themeCore">
        <div className="jobCreationBar bg-white px-4 py-6 rounded-lg flex justify-between items-center ">
          <div className="title text-themeBlue  font-medium text-3xl">
            Long Term Incentives
          </div>

          <div className="flex flex-row justify-between">
            {!plan_id ? (
              <>
                {checkPermission(permissions.CREATE_COMPANY_LTI) ||
                checkPermission(permissions.UPDATE_COMPANY_LTI) ? (
                  <CustomButton
                    onClick={() => setIsUploadModal(true)}
                    isLoading={isUploading}
                    btnName="Upload"
                    customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder"
                    iconPosition="left"
                  >
                    <MdOutlineFileUpload
                      size={20}
                      className="text-white font-bold"
                    />
                  </CustomButton>
                ) : null}

                <CustomButton
                  onClick={() => download()}
                  btnName="Download"
                  customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
                  iconPosition="right"
                >
                  <MdOutlineFileDownload
                    size={20}
                    className="text-white font-bold"
                  />
                </CustomButton>
              </>
            ) : null}

            <CustomButton
              onClick={() => setIsVersionsModal(true)}
              btnName="Versions"
              customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
              iconPosition="left"
            ></CustomButton>

            {checkPermission(permissions.CREATE_COMPANY_LTI) && (
              <CustomButton
                onClick={() => setIsVisible(true)}
                btnName=" Create Long Term Incentive"
                customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
                iconPosition="left"
              ></CustomButton>
            )}
          </div>
        </div>
      </div>
      <div className="jobCreationListing mt-10 bg-white px-14 py-6 rounded-lg">
        <SearchBox
          placeholder="Type to search..."
          value={searchInput}
          onChange={value => setSearchInput(value)}
        />
        <Row>
          <Table
            scroll={1500}
            data={data}
            columns={getColumns({
              isDeleting,
              longTermIncentive_id,
              removeLongTermIncentive,
              editLongTermIncentive,
              userRole: role,
            })}
            isLoading={isLoading || isFetchingPlanIncentives}
            pagination={true}
            count={pagination?.count}
            onChangePage={setPage}
            page={page}
            onPageSizeChange={setPageSize}
          />
        </Row>
      </div>
    </>
  );
};

export default LongTermIncentives;
